import { AlertTriangle, ShieldAlert, ShieldCheck, type LucideIcon } from "lucide-react";
import { Badge } from "./badge";
import type { StatusTone } from "@/components/status-badge";

/**
 * Canonical "automation risk" badge.
 *
 * Replaces the inline `isRisky = priority === "critical" || flags
 * includes "PAYMENT"` logic that lived in the issue-detail
 * `<StickyActionBar>` and never propagated to the patches detail
 * pane / run-fix button. With one badge vocabulary every surface
 * that gates an automated mutation tells the operator the same
 * story:
 *
 *   <RiskBadge level="low"    />  → "Safe to apply"   (success)
 *   <RiskBadge level="medium" />  → "Review first"     (warning)
 *   <RiskBadge level="high"   />  → "Risky — dry run"  (danger)
 *
 * Tier choices intentionally biased toward operator caution:
 * `medium` already nudges to a manual review, `high` to a dry-run
 * preview before any real change. Pages override the label per
 * locale; tone + icon are catalog-fixed.
 *
 * Pages compute the level themselves (per-domain rules: payment
 * flag for issues, deletion-heavy diff for patches, etc.) and pass
 * it; this component only renders.
 */

export type RiskLevel = "low" | "medium" | "high";

const LEVEL_TONE: Record<RiskLevel, StatusTone> = {
  low:    "success",
  medium: "warning",
  high:   "danger",
};

const LEVEL_ICON: Record<RiskLevel, LucideIcon> = {
  low:    ShieldCheck,
  medium: ShieldAlert,
  high:   AlertTriangle,
};

const LEVEL_DEFAULT_LABEL: Record<RiskLevel, string> = {
  low:    "Safe",
  medium: "Review",
  high:   "Risky",
};

export type RiskBadgeProps = {
  level: RiskLevel;
  /** Overrides the canonical label (i18n). */
  label?: string;
  /** Optional list of reason strings — rendered into the native
   *  `title` (hover tooltip) so the operator can see WHY the level
   *  was raised without us shipping a popover. */
  reasons?: ReadonlyArray<string>;
  className?: string;
};

export function RiskBadge({ level, label, reasons, className }: RiskBadgeProps) {
  const Icon = LEVEL_ICON[level];
  const tone = LEVEL_TONE[level];
  const tooltip = reasons && reasons.length > 0 ? reasons.join(" · ") : undefined;
  return (
    <span title={tooltip} className={className}>
      <Badge tone={tone} icon={Icon}>
        {label ?? LEVEL_DEFAULT_LABEL[level]}
      </Badge>
    </span>
  );
}
